/**
 * MapChart Component
 * Interactive map with farm locations, color-coded by SELECT question answers,
 * transparency based on NUMBER/DATE question, and supercluster clustering.
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import type L from "leaflet";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Slider,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import Supercluster from "supercluster";
import type { BBox } from "geojson";
import type {
  MapDataDto,
  MapPointDto,
  FarmSummaryDto,
} from "@/api/types/dtos/analytics/AnalyticsDto";
import { FarmListModal } from "../FarmListModal";
import {
  filterMapPoints,
  resolveColorValueLabel,
} from "../../utils/mapPointsFilter";
import "leaflet/dist/leaflet.css";

interface MapChartProps {
  data: MapDataDto;
}

interface ClusterProperties {
  cluster: boolean;
  pointCount: number;
  dominantColor: string;
  avgOpacity: number;
  colorBreakdown: Record<string, number>;
}

const DEFAULT_CENTER: [number, number] = [8.5, -80.0];

// "Sin respuesta" farms are temporarily hidden at client request; the backend still
// sends them. Set to true to restore the previous behavior.
const SHOW_NO_ANSWER = false;
const NO_ANSWER_OPTION_VALUE = "SIN_RESPUESTA";

const TILE_LAYERS = {
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
  },
  terrain: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
  },
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
} as const;

type TileLayerType = keyof typeof TILE_LAYERS;

function formatTransparencyValue(value: number, fieldType: string | null | undefined): string {
  if (fieldType === "DATE" || fieldType === "DATETIME") {
    const date = new Date(value * 86400000);
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function FitBoundsOnChange({ points }: { points: MapPointDto[] }) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    // Wait until the map is ready: fitting bounds on an unmeasured (zero-size)
    // container inside the dashboard grid produces a wrong initial view.
    map.whenReady(() => {
      if (points.length === 1) {
        map.setView([points[0].latitude, points[0].longitude], 13);
        return;
      }
      const lats = points.map((p) => p.latitude);
      const lngs = points.map((p) => p.longitude);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      map.fitBounds(
        [
          [minLat, minLng],
          [maxLat, maxLng],
        ],
        { padding: [40, 40] }
      );
    });
  }, [points, map]);

  return null;
}

function ZoomHandler({ onZoom }: { onZoom: (zoom: number, bounds: number[]) => void }) {
  const emitBounds = () => {
    const b = map.getBounds();
    onZoom(map.getZoom(), [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
  };

  const map = useMapEvents({
    moveend: emitBounds,
    // Re-emit when the container is (re)measured so cluster queries never run
    // against bounds captured from a zero-size map.
    resize: emitBounds,
  });

  useEffect(() => {
    // The container is often still unsized during the first layout pass, which made
    // the initial bounds emission miss the points until a manual zoom fired moveend.
    // invalidateSize forces Leaflet to re-measure; resize then re-emits real bounds.
    map.whenReady(() => {
      map.invalidateSize();
      emitBounds();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

export const MapChart: React.FC<MapChartProps> = ({ data }) => {
  const [zoom, setZoom] = useState(6);
  const [bounds, setBounds] = useState<BBox>([-90, -180, 90, 180]);
  const [visibleColors, setVisibleColors] = useState<Set<string>>(new Set());
  const [valueRange, setValueRange] = useState<[number, number] | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [tileLayer, setTileLayer] = useState<TileLayerType>("street");
  const [farmModalData, setFarmModalData] = useState<{
    farms: FarmSummaryDto[];
    title: string;
  } | null>(null);
  // Index is kept in state (not just a ref): each load produces a new object identity,
  // which forces the clusters memo to recompute. A boolean "ready" latch silently broke
  // this: reloads after the first kept the stale index until a manual pan/zoom.
  const [clusterIndex, setClusterIndex] = useState<Supercluster<
    MapPointDto,
    ClusterProperties
  > | null>(null);

  const allPoints = data?.points;
  const metadata = data?.metadata;

  const points = useMemo(
    () =>
      SHOW_NO_ANSWER
        ? (allPoints ?? [])
        : (allPoints ?? []).filter((p) => p.colorValue !== NO_ANSWER_OPTION_VALUE),
    [allPoints]
  );

  const colorLegend = useMemo(
    () =>
      SHOW_NO_ANSWER
        ? (metadata?.colorLegend ?? [])
        : (metadata?.colorLegend ?? []).filter((c) => c.optionValue !== NO_ANSWER_OPTION_VALUE),
    [metadata?.colorLegend]
  );

  // Initialize visible colors and value range from metadata
  useEffect(() => {
    if (colorLegend.length > 0) {
      setVisibleColors(new Set(colorLegend.map((c) => c.optionValue)));
    }
  }, [colorLegend]);

  useEffect(() => {
    if (metadata && metadata.minValue != null && metadata.maxValue != null) {
      setValueRange([metadata.minValue, metadata.maxValue]);
    }
  }, [metadata?.minValue, metadata?.maxValue]);

  // Filter points based on visible colors and value range
  const filteredPoints = useMemo(() => {
    return filterMapPoints(
      points,
      visibleColors,
      valueRange,
      colorLegend.length > 0
    );
  }, [points, visibleColors, valueRange, colorLegend]);

  // Build supercluster index
  const superclusterRef = useRef<Supercluster<MapPointDto, ClusterProperties> | null>(null);

  const geojsonPoints = useMemo(() => {
    return filteredPoints.map((p) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [p.longitude, p.latitude],
      },
      properties: p,
    }));
  }, [filteredPoints]);

  useEffect(() => {
    const index = new Supercluster<MapPointDto, ClusterProperties>({
      radius: 60,
      maxZoom: 18,
      map: (props) => ({
        cluster: false,
        pointCount: 1,
        dominantColor: props.color,
        avgOpacity: props.opacity,
        colorBreakdown: { [props.color]: 1 },
      }),
      reduce: (accumulated: ClusterProperties, props: ClusterProperties) => {
        accumulated.pointCount += props.pointCount;
        accumulated.avgOpacity =
          (accumulated.avgOpacity * (accumulated.pointCount - props.pointCount) +
            props.avgOpacity * props.pointCount) /
          accumulated.pointCount;

        for (const [color, count] of Object.entries(props.colorBreakdown)) {
          accumulated.colorBreakdown[color] = (accumulated.colorBreakdown[color] || 0) + count;
        }

        let maxCount = 0;
        for (const [color, count] of Object.entries(accumulated.colorBreakdown)) {
          if (count > maxCount) {
            maxCount = count;
            accumulated.dominantColor = color;
          }
        }
      },
    });

    index.load(geojsonPoints);
    superclusterRef.current = index;
    setClusterIndex(index);
  }, [geojsonPoints]);

  // Get clusters at current viewport
  const clusters = useMemo(() => {
    if (!clusterIndex) return [];
    return clusterIndex.getClusters(bounds, Math.floor(zoom));
  }, [clusterIndex, bounds, zoom]);

  const handleColorToggle = (optionValue: string) => {
    setVisibleColors((prev) => {
      const next = new Set(prev);
      if (next.has(optionValue)) {
        next.delete(optionValue);
      } else {
        next.add(optionValue);
      }
      return next;
    });
  };

  const handlePointClick = (point: MapPointDto) => {
    setFarmModalData({
      farms: [
        {
          farmId: point.farmId,
          farmName: point.farmName,
          value: resolveColorValueLabel(point.colorValue, colorLegend),
        },
      ],
      title: point.farmName,
    });
  };

  const handleClusterClick = (clusterId: number) => {
    if (!superclusterRef.current) return;
    const leaves = superclusterRef.current.getLeaves(clusterId, Infinity);
    const farms: FarmSummaryDto[] = leaves.map((leaf) => {
      const p = leaf.properties as unknown as MapPointDto;
      return {
        farmId: p.farmId,
        farmName: p.farmName,
        value: resolveColorValueLabel(p.colorValue, colorLegend),
      };
    });
    setFarmModalData({
      farms,
      title: `${farms.length} finca${farms.length !== 1 ? "s" : ""}`,
    });
  };

  if (!data || points.length === 0) {
    return (
      <Box
        sx={{
          height: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-secondary)",
        }}
      >
        {metadata && metadata.excludedNoCoordinates > 0
          ? `${metadata.excludedNoCoordinates} fincas sin coordenadas excluidas`
          : "No data available"}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: 500 }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={6}
        style={{ height: "100%", width: "100%", borderRadius: "8px", zIndex: 0 }}
        scrollWheelZoom
        whenReady={() => setMapReady(true)}
      >
        <TileLayer
          key={tileLayer}
          attribution={TILE_LAYERS[tileLayer].attribution}
          url={TILE_LAYERS[tileLayer].url}
        />
        <FitBoundsOnChange points={filteredPoints} />
        <ZoomHandler
          onZoom={(z, b) => {
            setZoom(z);
            setBounds(b as BBox);
          }}
        />

        {mapReady &&
          clusters.map((cluster) => {
            const [lng, lat] = cluster.geometry.coordinates;
            const props = cluster.properties as unknown as Record<string, unknown>;
            const isCluster = props.cluster as boolean;
            const pointCount = (props.pointCount as number) || 1;
            const color = (props.dominantColor as string) || (props.color as string) || "#999999";
            const opacity = (props.avgOpacity as number) ?? (props.opacity as number) ?? 1.0;

            if (isCluster) {
              const radius = Math.min(12 + Math.sqrt(pointCount) * 3, 40);
              return (
                <CircleMarker
                  key={`cluster-${cluster.id}`}
                  center={[lat, lng]}
                  radius={radius}
                  pathOptions={{
                    color: "#ffffff",
                    weight: 2,
                    fillColor: hexToRgba(color, opacity),
                    fillOpacity: 0.8,
                  }}
                  eventHandlers={{
                    click: (e: L.LeafletMouseEvent) => {
                      const expansionZoom = superclusterRef.current?.getClusterExpansionZoom(
                        cluster.id as number
                      );
                      if (expansionZoom != null && e.originalEvent.ctrlKey === false) {
                        const map = e.target._map;
                        if (map) {
                          map.setView([lat, lng], expansionZoom);
                        }
                      }
                    },
                    contextmenu: (e: L.LeafletMouseEvent) => {
                      e.originalEvent.preventDefault();
                      handleClusterClick(cluster.id as number);
                    },
                  }}
                >
                  <Tooltip sticky>
                    <div style={{ fontSize: "12px" }}>
                      <strong>{pointCount} fincas</strong>
                      {Object.entries((props.colorBreakdown as Record<string, number>) || {}).map(
                        ([c, cnt]) => (
                          <div
                            key={c}
                            style={{ display: "flex", alignItems: "center", gap: "4px" }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                width: 10,
                                height: 10,
                                backgroundColor: c,
                                borderRadius: "50%",
                              }}
                            />
                            {cnt}
                          </div>
                        )
                      )}
                      <div style={{ marginTop: 2, fontStyle: "italic", opacity: 0.7 }}>
                        Click para zoom | Click derecho para ver fincas
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            }

            const pointData = cluster.properties as unknown as MapPointDto;
            return (
              <CircleMarker
                key={`point-${pointData.farmId}`}
                center={[lat, lng]}
                radius={6}
                pathOptions={{
                  color: "#ffffff",
                  weight: 1.5,
                  fillColor: hexToRgba(pointData.color, pointData.opacity ?? 1.0),
                  fillOpacity: 0.85,
                }}
                eventHandlers={{
                  click: () => {
                    handlePointClick(pointData);
                  },
                }}
              >
                <Tooltip sticky>
                  <div style={{ fontSize: "12px" }}>
                    <strong>{pointData.farmName}</strong>
                    <br />
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        backgroundColor: pointData.color,
                        borderRadius: "50%",
                        marginRight: 4,
                      }}
                    />
                    {resolveColorValueLabel(pointData.colorValue, colorLegend)}
                    {pointData.transparencyValue != null && (
                      <>
                        <br />
                        {metadata?.transparencyLabel || "Valor"}:{" "}
                        {formatTransparencyValue(
                          pointData.transparencyValue,
                          metadata?.transparencyFieldType
                        )}
                      </>
                    )}
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}
      </MapContainer>

      {/* Map Layer Switcher - Bottom Right */}
      <Paper
        elevation={3}
        sx={{
          position: "absolute",
          bottom: 10,
          right: 10,
          zIndex: 1000,
          p: 0.5,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "8px",
        }}
      >
        <ToggleButtonGroup
          value={tileLayer}
          exclusive
          size="small"
          onChange={(_, val) => val && setTileLayer(val)}
        >
          <ToggleButton value="satellite" sx={{ px: 1, py: 0.25, fontSize: "0.7rem" }}>
            Satelite
          </ToggleButton>
          <ToggleButton value="terrain" sx={{ px: 1, py: 0.25, fontSize: "0.7rem" }}>
            Terreno
          </ToggleButton>
          <ToggleButton value="street" sx={{ px: 1, py: 0.25, fontSize: "0.7rem" }}>
            Calles
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Legend Panel - Top Right */}
      {metadata && (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1000,
            p: 1.5,
            maxWidth: 250,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: "8px",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
            Colores ({colorLegend.length})
          </Typography>
          {colorLegend.map((item) => (
            <Box
              key={item.optionValue}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: item.color,
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {item.optionLabel}
              </Typography>
              <Chip label={item.count} size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
            </Box>
          ))}

          {metadata.minValue != null && metadata.maxValue != null && metadata.transparencyLabel && (
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                {metadata.transparencyLabel}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="textSecondary">
                  {formatTransparencyValue(metadata.minValue, metadata.transparencyFieldType)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatTransparencyValue(metadata.maxValue, metadata.transparencyFieldType)}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 4,
                  background: "linear-gradient(to right, rgba(0,0,0,0.3), rgba(0,0,0,0))",
                  mt: 0.5,
                }}
              />
            </Box>
          )}

          {metadata.excludedNoCoordinates > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                {metadata.excludedNoCoordinates} sin coordenadas
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Dynamic Filter Panel - Top Left */}
      {colorLegend.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 1000,
            p: 1.5,
            maxWidth: 220,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: "8px",
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
            Filtros
          </Typography>
          {colorLegend.map((item) => (
            <FormControlLabel
              key={item.optionValue}
              control={
                <Checkbox
                  checked={visibleColors.has(item.optionValue)}
                  onChange={() => handleColorToggle(item.optionValue)}
                  size="small"
                  sx={{
                    color: item.color,
                    "&.Mui-checked": { color: item.color },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                  {item.optionLabel} ({item.count})
                </Typography>
              }
              sx={{ display: "flex", alignItems: "center", ml: 0 }}
            />
          ))}

          {valueRange &&
            metadata.minValue != null &&
            metadata.maxValue != null &&
            metadata.minValue !== metadata.maxValue && (
              <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 1 }}>
                  Rango de valores
                </Typography>
                <Slider
                  value={valueRange}
                  onChange={(_, newValue) => setValueRange(newValue as [number, number])}
                  min={metadata.minValue}
                  max={metadata.maxValue}
                  size="small"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) =>
                    formatTransparencyValue(v, metadata?.transparencyFieldType)
                  }
                />
              </Box>
            )}
        </Paper>
      )}

      {/* Farm List Modal for drill-down */}
      {farmModalData && (
        <FarmListModal
          open={!!farmModalData}
          farms={farmModalData.farms}
          title={farmModalData.title}
          onClose={() => setFarmModalData(null)}
        />
      )}
    </Box>
  );
};
